import type { TitleSize } from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { Skeleton } from '@tempo-io/sui-skeleton';
import { differenceInDays, isBefore } from 'date-fns';
import { type Dispatch, memo, type SetStateAction, useMemo, useRef } from 'react';
import { getItemTitleFontSize } from '@/common/components/ui/swimlane/layout/body/cards/CardUtils';
import type { LabelWithField } from '@/common/components/ui/swimlane/types';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import {
  getGoverningDeadline,
  getIsDeadlineAtRisk,
} from '@/features/planner/components/common/lib/getIsAtRisk';
import { progressFieldIdToProgress, systemProgressToProgress } from '../utils/itemProgressUtils';
import { DEADLINE_PILL_HEIGHT } from './TimelineDeadlineMarker';
import { TimelineItem } from './TimelineItem';
import { ITEM_RIGHT_INSET, MIN_ITEM_WIDTH } from './TimelineItemConstants';
import type {
  TimelineItemType,
  TimelineRowType,
  TimelineScenarioContext,
} from './useTimelineTableColumns';
import { lightenTimelineBgColor } from './utils/timelineColorUtils';
import { getNormalizePlannedDates } from './utils/timelineDateUtils';
import {
  calculateItemWidth,
  calculateMaxContainerHeight,
  calculateUncertaintyTailWidth,
  clampEndDateToWindow,
  clampStartDateToWindow,
  getItemHeight,
  getSingleLaneHeight,
  isOutsideTimelineWindow,
} from './utils/timelineGeometryUtils';
import { stackTimelineItems } from './utils/timelineStackingUtils';

const LOADING_SKELETON_BARS = [
  { id: 'a', basis: '55%' },
  { id: 'b', basis: '35%' },
] as const;

export const TimelineItemContainer = memo(
  ({
    isCompact,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    timelineStartDate,
    timelineEndDate,
    item,
    isExpanded,
    setExpandedChildren,
    color,
    dayWidth,
    titleSize,
    intervalStart,
    onResizeEnd,
    onItemClick,
    row,
    itemColorById,
    containerTopOffset,
    progressFieldId,
    systemProgressMap,
    hideName = false,
    isPublishedMode = false,
    showDependencyDots = true,
    showDates = true,
    showUncertainty = false,
    showDeadlineMarkers = true,
    timelineItemClassName,
    timelineItemTextColor,
    itemLabelsById,
    alignNameLeft = false,
    isSelected = false,
    isInMultiSelectDrag = false,
    isSettling = false,
    getSelectedItemIds,
    onSelectionClick,
    scenarioContext,
  }: {
    isCompact: boolean;
    canResize: boolean;
    canDragBetweenHeaders: boolean;
    canDragDates: boolean;
    timelineStartDate: string;
    timelineEndDate: string;
    item: TimelineItemType;
    isExpanded: boolean;
    setExpandedChildren: Dispatch<
      SetStateAction<{
        [key: number]: boolean;
      }>
    >;
    color: string;
    dayWidth: number;
    titleSize: TitleSize;
    intervalStart: Date;
    onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
    onItemClick: (itemId: number) => void;
    row?: TimelineRowType;
    itemColorById?: Record<number, string>;
    containerTopOffset: number;
    progressFieldId: number | null;
    systemProgressMap: Map<number, number> | null;
    hideName?: boolean;
    isPublishedMode?: boolean;
    showDependencyDots?: boolean;
    showDates?: boolean;
    showUncertainty?: boolean;
    showDeadlineMarkers?: boolean;
    timelineItemClassName?: string;
    timelineItemTextColor?: string;
    itemLabelsById?: Map<number, LabelWithField[]>;
    alignNameLeft?: boolean;
    isSelected?: boolean;
    isInMultiSelectDrag?: boolean;
    isSettling?: boolean;
    getSelectedItemIds?: () => number[] | null;
    onSelectionClick?: (
      itemId: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
    ) => boolean;
    scenarioContext?: TimelineScenarioContext;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const isPlannerRiskAlertsEnabled = useFlag('plannerRiskAlerts');

    const {
      hasAnyDate: hasAnyPlannedDate,
      start: normalizedStart,
      finish: normalizedFinish,
    } = getNormalizePlannedDates(item.plannedStartDateTime, item.plannedFinishDateTime);

    const startDateToUse = clampStartDateToWindow(normalizedStart, timelineStartDate);
    const endDateToUse = clampEndDateToWindow(normalizedFinish, timelineEndDate);

    const calculatedWidth = calculateItemWidth(startDateToUse, endDateToUse, dayWidth);
    const renderedWidth = Math.max(calculatedWidth - ITEM_RIGHT_INSET, MIN_ITEM_WIDTH);

    const uncertaintyWidth = showUncertainty
      ? calculateUncertaintyTailWidth({
          latestFinishDateTime: item.latestFinishDateTime,
          finishDateToUse: endDateToUse,
          timelineEndDate,
          dayWidth,
        })
      : 0;
    const normalizedLatestFinish = item.latestFinishDateTime?.split('T')[0];
    const isLatestFinishClamped =
      uncertaintyWidth > 0 && !!normalizedLatestFinish && normalizedLatestFinish > timelineEndDate;
    const containerWidth = renderedWidth + uncertaintyWidth;

    const governingDeadline = getGoverningDeadline({
      ownDeadline: item.deadlineDateTime,
      ancestorDeadline: isPlannerRiskAlertsEnabled ? item.earliestAncestorDeadlineDateTime : null,
      ancestorSourceItemName: isPlannerRiskAlertsEnabled
        ? item.earliestAncestorDeadlineSourceItemName
        : null,
    });

    const isPastGoverningDeadline = getIsDeadlineAtRisk({
      forecastedFinish: item.plannedFinishDateTime,
      deadline: governingDeadline.deadline,
    });
    const isAtRisk = showUncertainty && isPastGoverningDeadline;

    const labels = itemLabelsById?.get(item.id);
    const singleLaneHeight = getSingleLaneHeight(isCompact);
    const defaultItemHeight = getItemHeight(isCompact);
    const bottomGap = (singleLaneHeight - defaultItemHeight) / 2;
    const hasDeadlineMarker =
      showDeadlineMarkers &&
      showUncertainty &&
      scenarioContext !== undefined &&
      !!item.deadlineDateTime;
    const itemTopOffset = hasDeadlineMarker ? DEADLINE_PILL_HEIGHT : bottomGap;
    const itemHeight = hasDeadlineMarker
      ? singleLaneHeight - bottomGap - DEADLINE_PILL_HEIGHT
      : defaultItemHeight;
    const progress = systemProgressMap
      ? systemProgressToProgress(item.id, systemProgressMap)
      : progressFieldIdToProgress(progressFieldId ?? null, item);
    const progressPercentage: number | null =
      progress !== null ? Math.round(progress * 100) : progressFieldId !== null ? 0 : null;

    const leftOffset = differenceInDays(startDateToUse, intervalStart) * dayWidth;
    const fontSize = getItemTitleFontSize(titleSize);

    const { stackedItems: stackedChildren, highestOrder: maxChildOrder } = useMemo(() => {
      return item.children && item.children.length > 0
        ? stackTimelineItems(item.children)
        : { stackedItems: [], highestOrder: 0 };
    }, [item.children]);

    if (
      isOutsideTimelineWindow(
        hasAnyPlannedDate,
        startDateToUse,
        endDateToUse,
        timelineStartDate,
        timelineEndDate,
      )
    ) {
      return null;
    }

    const isLoadingChildren = Boolean(item.isLoadingChildren) && stackedChildren.length === 0;
    const hasExpandedContent = stackedChildren.length > 0 || isLoadingChildren;

    const maxContainerHeight = calculateMaxContainerHeight(
      singleLaneHeight,
      maxChildOrder,
      hasExpandedContent,
    );
    const containerHeight = isExpanded ? maxContainerHeight : singleLaneHeight;

    const hasChildren = item.hasChildren ?? (item.children && item.children.length > 0);

    return (
      <div
        ref={itemRef}
        className={cn(
          'absolute overflow-visible transition-[height] duration-50 ease-in-out',
          isExpanded ? 'z-10' : 'z-5',
        )}
        style={{
          height: containerHeight,
          maxHeight: maxContainerHeight,
          minHeight: singleLaneHeight,
          left: leftOffset,
          width: containerWidth,
          top: containerTopOffset,
        }}
      >
        <TimelineItem
          item={item}
          color={color}
          isCompact={isCompact}
          itemTopOffset={itemTopOffset}
          calculatedWidth={renderedWidth}
          itemHeight={itemHeight}
          progress={progressPercentage}
          fontSize={fontSize}
          timelineStartDate={timelineStartDate}
          timelineEndDate={timelineEndDate}
          startDateToUse={startDateToUse}
          endDateToUse={endDateToUse}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          onExpandToggle={(e) => {
            e.stopPropagation();
            // Single expansion: opening one parent collapses any other.
            setExpandedChildren(() => (isExpanded ? {} : { [item.id]: true }));
          }}
          canResize={canResize}
          canDragBetweenHeaders={canDragBetweenHeaders}
          canDragDates={canDragDates}
          isChild={false}
          onItemClick={onItemClick}
          dayWidth={dayWidth}
          onResizeEnd={onResizeEnd}
          row={row}
          hideName={hideName}
          isPublishedMode={isPublishedMode}
          showDependencyDots={showDependencyDots}
          showDates={showDates}
          showUncertainty={showUncertainty}
          uncertaintyWidth={uncertaintyWidth}
          latestFinishDate={item.latestFinishDateTime}
          isTailClamped={isLatestFinishClamped}
          isAtRisk={isAtRisk}
          timelineItemClassName={timelineItemClassName}
          timelineItemTextColor={timelineItemTextColor}
          labels={labels}
          alignNameLeft={alignNameLeft}
          isSelected={isSelected}
          isInMultiSelectDrag={isInMultiSelectDrag}
          isSettling={isSettling}
          getSelectedItemIds={getSelectedItemIds}
          onSelectionClick={onSelectionClick}
          scenarioContext={scenarioContext}
        />

        {isExpanded && (
          <div className="absolute left-0 top-0 w-full">
            {stackedChildren.map((child) => {
              const childDates = getNormalizePlannedDates(
                child.plannedStartDateTime,
                child.plannedFinishDateTime,
              );
              if (!childDates.hasAnyDate) {
                return null;
              }
              const childNS = childDates.start as string;
              const childNF = childDates.finish as string;

              const childStartDate = isBefore(childNS, timelineStartDate)
                ? timelineStartDate
                : childNS;

              const childEndDate = isBefore(childNF, timelineEndDate) ? childNF : timelineEndDate;

              if (
                isBefore(new Date(timelineEndDate), new Date(childStartDate)) ||
                isBefore(new Date(childEndDate), new Date(timelineStartDate))
              ) {
                return null;
              }

              const childNumDays = differenceInDays(childEndDate, childStartDate) + 1;
              const childWidth = Math.max(dayWidth * childNumDays, MIN_ITEM_WIDTH);
              const childLeftOffset = differenceInDays(childStartDate, intervalStart) * dayWidth;
              const childLabels = itemLabelsById?.get(child.id);
              const childItemHeight = getItemHeight(isCompact);
              const childTopOffset =
                (child.order || 0) * singleLaneHeight + (singleLaneHeight - childItemHeight) / 2;
              const colorToUse = itemColorById?.[child.id] || color;
              const childColor = lightenTimelineBgColor(colorToUse, 30);
              const childCanResize = canResize;
              const childCanDragBetweenHeaders = canDragBetweenHeaders;
              const childCanDragDates = canDragDates;
              const childRawProgress = systemProgressMap
                ? systemProgressToProgress(child.id, systemProgressMap)
                : progressFieldIdToProgress(progressFieldId ?? null, child);
              const childProgressPercentage: number | null =
                childRawProgress !== null
                  ? Math.round(childRawProgress * 100)
                  : progressFieldId !== null
                    ? 0
                    : null;

              return (
                <div
                  key={child.id}
                  className="absolute"
                  style={{
                    left: childLeftOffset - leftOffset,
                    width: childWidth,
                    top: singleLaneHeight,
                    height: singleLaneHeight,
                  }}
                >
                  <TimelineItem
                    item={child}
                    color={childColor}
                    isCompact={isCompact}
                    itemTopOffset={childTopOffset}
                    itemHeight={childItemHeight}
                    progress={childProgressPercentage}
                    fontSize={fontSize}
                    timelineStartDate={timelineStartDate}
                    timelineEndDate={timelineEndDate}
                    startDateToUse={childStartDate}
                    endDateToUse={childEndDate}
                    canResize={childCanResize}
                    canDragBetweenHeaders={childCanDragBetweenHeaders}
                    canDragDates={childCanDragDates}
                    isChild
                    onItemClick={onItemClick}
                    dayWidth={dayWidth}
                    calculatedWidth={childWidth}
                    hideName={hideName}
                    isPublishedMode={isPublishedMode}
                    showDependencyDots={showDependencyDots}
                    showDates={showDates}
                    showUncertainty={showUncertainty}
                    timelineItemClassName={timelineItemClassName}
                    timelineItemTextColor={timelineItemTextColor}
                    labels={childLabels}
                    alignNameLeft={alignNameLeft}
                    scenarioContext={scenarioContext}
                  />
                </div>
              );
            })}

            {isLoadingChildren && (
              <div
                className="absolute flex gap-1"
                style={{
                  left: 0,
                  width: Math.max(renderedWidth, MIN_ITEM_WIDTH),
                  top: singleLaneHeight + itemTopOffset,
                  height: itemHeight,
                }}
              >
                {LOADING_SKELETON_BARS.map((bar) => (
                  <Skeleton
                    key={`skeleton-${item.id}-${bar.id}`}
                    className={cn('h-full', isCompact ? 'rounded-sm' : 'rounded-md')}
                    style={{
                      flexBasis: bar.basis,
                      backgroundColor: lightenTimelineBgColor(color, 30),
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

TimelineItemContainer.displayName = 'TimelineItemContainer';
